"use client";

// Top-bar notification dropdown for in-flight trades.
//
// Lives in the global nav. Renders nothing for visitors with no
// pending trades, so the chrome stays clean for first-time / logged-out
// users. When there ARE pending trades, shows a bell with a red count
// badge; clicking opens a small popover listing the trades with a
// Resume link to each.
//
// Identity comes from the same session cookie used everywhere else; the
// pending-mine endpoint already returns an empty list when no identity
// is present, so an unauthenticated visitor gets the silent path.

import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import { Bell, ArrowRight, Clock } from "lucide-react";
import { formatLocalAmount } from "@/lib/locale";

type PendingTrade = {
  id: string;
  cryptoType: string;
  cryptoAmount: number;
  network: string;
  localCurrency: string;
  localAmount: number;
  status: string;
  expiresAt: string | null;
  createdAt: string;
};

const STATUS_LABEL: Record<string, string> = {
  pending_deposit:       "Waiting for deposit",
  deposit_confirmed:     "Deposit received",
  processing_payout:     "Sending payout",
  awaiting_approval:     "Manual review",
  awaiting_verification: "Verification needed",
  action_required:       "Action required",
};

// Tone for the bell badge — most pending trades are pending_deposit
// (yellow); action_required gets the danger tone so it stands out;
// everything else stays neutral-blue.
const BADGE_TONE: Record<string, string> = {
  action_required: "bg-red-500",
  pending_deposit: "bg-amber-500",
};

export function PendingTradeNotification() {
  const [trades, setTrades] = useState<PendingTrade[] | null>(null);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  // Re-poll every 60s so the badge clears reasonably quickly after the
  // trade flips to a terminal state. Cheap query (indexed on session
  // token) so polling at this cadence is fine.
  useEffect(() => {
    let cancelled = false;
    const fetchOnce = () => {
      fetch("/api/v1/transactions/pending-mine", { cache: "no-store" })
        .then((r) => r.json())
        .then((d) => {
          if (cancelled) return;
          setTrades(Array.isArray(d?.trades) ? d.trades : []);
        })
        .catch(() => {
          // Silent — banner is opportunistic. Don't ruin the nav with
          // an error state for a 503.
          if (!cancelled) setTrades([]);
        });
    };
    fetchOnce();
    const id = setInterval(fetchOnce, 60_000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  // Click-outside / escape to close. Mounted only while open so we
  // don't pay the listener cost when the popover is dormant.
  useEffect(() => {
    if (!open) return;
    function onPointer(e: MouseEvent) {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // Hidden entirely until we have data + at least one trade. Avoids a
  // dormant bell sitting on the nav for everyone else.
  if (!trades || trades.length === 0) return null;

  // Pick the most-urgent status to colour the dot. action_required wins
  // over pending_deposit wins over the rest.
  const dotTone =
    BADGE_TONE[trades.find((t) => t.status === "action_required")?.status ?? ""] ??
    BADGE_TONE[trades.find((t) => t.status === "pending_deposit")?.status ?? ""] ??
    "bg-blue-500";

  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        aria-label={`${trades.length} pending trade${trades.length === 1 ? "" : "s"}`}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="relative inline-flex h-9 w-9 items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
      >
        <Bell size={18} />
        {/* Count badge.
            ─ Anchored top-right with a translate offset so the badge
              sits half-outside the bell's corner — consistent on every
              breakpoint, no negative-margin trick that drifts when the
              parent's gap tightens on phones.
            ─ ring-2 ring-background creates a "cutout" against the bell
              so a red badge over a dark/light bell never looks merged.
            ─ Min-w == min-h means single digits render as a circle and
              "9+" widens to a pill — both centered, no jumping.
            ─ leading-none keeps the digit vertically centered (without
              this, the line-box adds ~2px of bottom padding inside the
              badge and the number drifts upward).
            ─ >9 collapses to 9+ so the badge never exceeds 2 chars. */}
        <span
          className={`absolute top-0 right-0 translate-x-1/3 -translate-y-1/3 inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full px-1 text-[10px] font-bold leading-none text-white ring-2 ring-background ${dotTone}`}
        >
          {trades.length > 9 ? "9+" : trades.length}
        </span>
      </button>

      {open && (
        <div
          role="menu"
          // Positioning model differs by breakpoint.
          //
          // ─ Mobile (<sm): `fixed` so the popover anchors to the viewport,
          //   not the bell. The bell sits in the top-right corner of a
          //   narrow phone, so a 320px-wide `absolute right-0` popover
          //   ran off the LEFT edge of the screen (clipping "PENDING
          //   TRADE" to "DING TRADE"). `inset-x-2 top-16` pins it to
          //   the top-right area with 8px breathing room on both sides,
          //   filling the available width naturally.
          //
          // ─ Desktop (sm+): revert to the original bell-anchored
          //   `absolute right-0 mt-2 w-80` so the popover hangs cleanly
          //   under the bell. `sm:inset-x-auto` resets the mobile
          //   horizontal pinning so right-0 takes effect.
          //
          // Click-outside still works — the popover stays a DOM child
          // of `wrapRef`, only its visual frame moves.
          className="fixed top-16 inset-x-2 sm:absolute sm:inset-x-auto sm:right-0 sm:top-auto sm:mt-2 sm:w-80 rounded-xl border border-border bg-background shadow-lg overflow-hidden z-50"
        >
          <div className="px-4 py-3 border-b border-border bg-muted/40">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {trades.length === 1 ? "1 pending trade" : `${trades.length} pending trades`}
            </p>
          </div>

          <div className="max-h-[60vh] overflow-y-auto divide-y divide-border">
            {trades.map((t) => (
              <Link
                key={t.id}
                href={`/transactions/${t.id}`}
                onClick={() => setOpen(false)}
                className="flex items-center justify-between gap-3 px-4 py-3 hover:bg-muted transition-colors"
              >
                <div className="min-w-0 flex-1 space-y-1">
                  <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                    <Clock size={10} />
                    {STATUS_LABEL[t.status] ?? t.status}
                  </div>
                  <p className="text-sm font-semibold truncate">
                    {fmtAmount(t.cryptoAmount)} {t.cryptoType} → {t.localCurrency}{" "}
                    {formatLocalAmount(t.localAmount, t.localCurrency)}
                  </p>
                  <p className="text-[11px] text-muted-foreground">
                    {t.network} · Started {fmtRelative(t.createdAt)}
                    {t.expiresAt && t.status === "pending_deposit" && ` · Expires ${fmtTime(t.expiresAt)}`}
                  </p>
                </div>
                <ArrowRight size={14} className="text-muted-foreground shrink-0" />
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Format helpers — kept local so the component is self-contained.

function fmtAmount(n: number): string {
  return n.toFixed(8).replace(/\.?0+$/, "");
}

function fmtRelative(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const min = Math.floor(ms / 60_000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  const hours = Math.floor(min / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function fmtTime(iso: string): string {
  return new Date(iso).toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
  });
}
